import { asWidgetProps, asTrackingPayload } from 'Src/types/widget-helpers';
import React, { ComponentProps, Fragment, memo } from 'react';
import _, { groupBy, toPairs, sortBy, reverse } from 'lodash';
import { COLORS } from 'Components/utils/color/colors';
import { t } from 'Src/i18n';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { momentFromApiDate } from 'Utils/date/apiDate';
import { Button } from 'Components/frameworks/button/Button';
import { Title } from 'Components/frameworks/title/Title';
import { AccountSection } from 'Components/widgets/account-section/AccountSection';
import { BookingTeaser } from 'Components/widgets/booking-teaser/BookingTeaser';

const yearAggregation: (arg0: BookingEntity) => number = ({ dates: { startDate } }) =>
  momentFromApiDate(startDate).year();

const yearOrdering: (arg0: [string, Array<BookingEntity>]) => string = ([year]) => year;

const listBookingsByYear = (
  bookings: Array<BookingEntity>,
): Array<[string, Array<BookingEntity>]> =>
  reverse(sortBy(toPairs(groupBy(bookings, yearAggregation)), yearOrdering));
export type Props = {
  bookings: Array<BookingEntity>;
  currentPage: number;
  fetchMoreTrips: (
    arg0: BookingsPeriods,
    arg1: number,
    arg2: number,
    arg3: boolean,
    arg4: boolean,
  ) => void;
  fetchPeriod: BookingsPeriods;
  forwardRef: ReactRef;
  hasMoreTrips: boolean;
  id: string;
  isCanceled: boolean;
  isLoading: boolean;
  isYearDisplayed: boolean;
  limit: number;
  shouldProposeUpsells: boolean;
  title: string;
};

const _StaysSection = ({
  bookings,
  currentPage,
  fetchMoreTrips,
  fetchPeriod,
  forwardRef,
  hasMoreTrips,
  id,
  isCanceled,
  isLoading,
  isYearDisplayed,
  limit,
  shouldProposeUpsells,
  title,
}: Props) => {
  const { trackClickEvent } = useTrackEvent();
  const isPastBookings = fetchPeriod === 'past';
  const isFutureBookings = fetchPeriod === 'future';
  const orderedBookings = isFutureBookings
    ? listBookingsByYear(bookings).reverse()
    : listBookingsByYear(bookings);
  return (
    <AccountSection
      {...asWidgetProps<ComponentProps<typeof AccountSection>>({ id: id, key: id, title: title })}
    >
      {orderedBookings.map(([year, yearBookings]) => (
        <Fragment key={year}>
          {isYearDisplayed && (
            <Title
              {...asWidgetProps<ComponentProps<typeof Title>>({
                className: 'mb-10',
                hLevel: 3,
                theme: 'title-brand-n2',
              })}
            >
              {year}
            </Title>
          )}
          {yearBookings.map((yearBooking, idx2) => (
            <Fragment key={idx2}>
              <BookingTeaser
                {...asWidgetProps<ComponentProps<typeof BookingTeaser>>({
                  booking: yearBooking,
                  className: 'mb-20 last:mb-0',
                  dataTestId: 'Booking-Remote',
                  forwardRef: forwardRef,
                })}
              />
            </Fragment>
          ))}
        </Fragment>
      ))}
      {hasMoreTrips && isPastBookings && (
        <div className="flex justify-center">
          <Button
            {...asWidgetProps<ComponentProps<typeof Button>>({
              color: COLORS.SIENNA,
              colorBg: COLORS.TRANSPARENT,
              colorBorder: COLORS.SIENNA,
              isLoading: isLoading,
              onClick: () => {
                trackClickEvent(
                  TRACK_EVENT_NAMES.CLICK_CA_REMOTE,
                  asTrackingPayload({
                    detail_click: 'load_more_stays',
                  }),
                );
                fetchMoreTrips(
                  fetchPeriod,
                  limit,
                  currentPage + 1,
                  isCanceled,
                  shouldProposeUpsells,
                );
              },
              size: 'medium',
            })}
          >
            {t('customerAccount.dashboard.stays.loadMoreStays')}
          </Button>
        </div>
      )}
    </AccountSection>
  );
};

export const StaysSection = memo<Props>(_StaysSection);
